import { Directive, ElementRef, Input, OnDestroy, OnInit, ViewContainerRef } from '@angular/core'
import { ToolbarSearchResultsComponent } from './toolbar-search-results.component'
import { fromEvent, Observable, Subject, merge } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay'
import { TemplatePortal } from '@angular/cdk/portal'

@Directive({
  selector: 'input[linToolbarSearchResults]',
  standalone: false,
})
export class ToolbarSearchResultsTriggerDirective implements OnInit, OnDestroy {
  onDestroy$ = new Subject<void>()
  overlayDestroyed$ = new Subject<void>()

  @Input() linToolbarSearchResults: ToolbarSearchResultsComponent

  private overlayRef: OverlayRef

  constructor(
    private host: ElementRef<HTMLInputElement>,
    private viewContainerRef: ViewContainerRef,
    private overlay: Overlay,
  ) {}

  ngOnInit() {
    merge(fromEvent(this.host.nativeElement, 'focus'), fromEvent(this.host.nativeElement, 'input'))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.openDropDown())
  }

  openDropDown() {
    if (this.overlayRef) {
      return
    }

    const { template } = this.linToolbarSearchResults
    const portal = new TemplatePortal(template, this.viewContainerRef)
    const overLayRef = (this.overlayRef = this.overlay.create(this.getOverlayConfig()))
    overLayRef.attach(portal)

    this.attachClosingActions()
    this.linToolbarSearchResults.opened.emit()
  }

  attachClosingActions() {
    merge(this.getOutsideClick$())
      .pipe(takeUntil(this.overlayDestroyed$))
      .subscribe(() => this.destroyOverlay())
  }

  destroyOverlay() {
    if (!this.overlayRef) {
      return
    }

    this.overlayRef.detach()
    this.overlayRef = null
    this.overlayDestroyed$.next()
  }

  private getOutsideClick$() {
    return merge(fromEvent(document, 'click') as Observable<MouseEvent>, fromEvent(document, 'touchend') as Observable<TouchEvent>).pipe(
      filter((event) => {
        if (!this.overlayRef) {
          return false
        }

        const target = event.target as HTMLElement
        const insideOverlay = this.overlayRef.overlayElement.contains(target)
        const insideOrigin = this.host.nativeElement.contains(target)

        return !insideOverlay && !insideOrigin
      }),
    )
  }

  private getOverlayConfig() {
    return new OverlayConfig({
      positionStrategy: this.getOverlayPosition(),
    })
  }

  private getOverlayPosition() {
    const belowPosition: ConnectedPosition = {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    }

    return this.overlay
      .position()
      .flexibleConnectedTo(this.host)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions([belowPosition])
  }

  ngOnDestroy() {
    this.destroyOverlay()
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
